import {
  RULES_WITH_DEDICATED_SECTION,
  blockingValidationErrorsAtom,
} from '@/states/blocking-validation-errors.state';
import { hasDigitalProductsWithoutCouponCodesAtom } from '@/states/has-digital-products-without-coupon-codes.state';
import {
  hasDuplicateVariantFixAtom,
  invalidGtinObjectItemIdsAtom,
  missingCategoryObjectItemIdsAtom,
  missingShippingObjectItemIdsAtom,
} from '@/states/object-anomalies.state';
import { psychologicalPriceErrorItemIdsAtom } from '@/states/psychological-price-errors.state';
import { vatRatesAtom } from '@/states/vat-rates.state';
import { atom } from 'jotai';

/**
 * Number of **blocking** anomaly types ("À corriger avant validation") surfaced
 * in the AlertSidebar: each maps to a distinct card that prevents closing the
 * offer. Blocking validation errors count once per distinct rule; the front-end
 * anomalies (missing category / shipping, duplicate variants, missing coupon
 * codes) count once each when present.
 *
 * Counting **types** (not products) is intentional: a single product can carry
 * several anomalies and the "products concerned" of different types overlap, so
 * they must never be summed across types.
 */
export const blockingAnomalyTypesCountAtom = atom<number>((get) => {
  const blockingErrorsCount = get(blockingValidationErrorsAtom).filter(
    (error) => !RULES_WITH_DEDICATED_SECTION.has(error.ruleId)
  ).length;

  return (
    blockingErrorsCount +
    (get(missingCategoryObjectItemIdsAtom).length > 0 ? 1 : 0) +
    (get(missingShippingObjectItemIdsAtom).length > 0 ? 1 : 0) +
    (get(hasDuplicateVariantFixAtom) ? 1 : 0) +
    (get(hasDigitalProductsWithoutCouponCodesAtom) ? 1 : 0)
  );
});

/**
 * Number of **non-blocking** anomaly types ("À vérifier") surfaced in the
 * AlertSidebar: recommended checks that do not prevent closing the offer
 * (psychological prices, invalid GTINs, multiple VAT rates). Counted by type,
 * like {@link blockingAnomalyTypesCountAtom}.
 */
export const toCheckAnomalyTypesCountAtom = atom<number>((get) => {
  const { uniqueVatRates } = get(vatRatesAtom);

  return (
    (get(psychologicalPriceErrorItemIdsAtom).length > 0 ? 1 : 0) +
    (get(invalidGtinObjectItemIdsAtom).length > 0 ? 1 : 0) +
    (uniqueVatRates.length > 1 ? 1 : 0)
  );
});
